import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useAuthStore } from '@/features/auth/store/auth';
import { hasPlatformAccess } from '@/features/auth/services/auth';
import { ChangeAppReceiver } from '@/shared/lib/change-app';
import { classifyCallbackError } from './callbackError';
import type { CallbackErrorKind } from './callbackError';
import './SsoCallbackPage.css';

/** Milliseconds before announcing that the exchange is taking longer. */
const SLOW_EXCHANGE_MS = 8000;

/**
 * SsoCallbackPage
 *
 * Federated (SSO/SAML) login callback. After the user authenticates on the
 * organization's IdP, IAM redirects the browser here with a single-use,
 * short-lived `minitoken` query parameter. This page:
 *
 * 1. Extracts `minitoken` (and optional `originClientId`/`userId`) from the URL
 * 2. Exchanges it via `POST /user-identities/renew-app-token?clientId=...&remember-me=true`
 *    (body: `validationToken`, `originClientId`, `userId`) — the "change app
 *    token" swap — using {@link ChangeAppReceiver.renewFromUrl}
 * 3. Persists the session through the auth store (`loginCompleted`), which is
 *    the same bootstrap as the credential login
 * 4. Strips the token parameters from the URL and navigates home, where the
 *    regular `GET /auth/user-info` bootstrap runs
 *
 * ## Error taxonomy (CA11–CA14)
 *
 * Failures are mapped by {@link classifyCallbackError} so the user is guided
 * to the right owner: missing authorization (company access manager) is never
 * presented as invalid credentials, IdP unavailability is explicitly not a
 * platform problem, and an expired return restarts in a single action.
 *
 * The route lives outside PublicRoute/PrivateRoute (like `/join`) so neither
 * guard bounces the browser away before the swap finishes.
 *
 * @returns The SSO callback page (spinner while exchanging, error state on failure)
 * @since 1.0.0
 * @category Pages
 */
const SsoCallbackPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [errorKind, setErrorKind] = useState<CallbackErrorKind | null>(null);
  const [slow, setSlow] = useState(false);
  // Defeat React 19 StrictMode double-effect: the minitoken is single-use,
  // so the exchange must run exactly once.
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (hasCalledApi.current) return;
    hasCalledApi.current = true;

    // Nenhum estado de carregamento é definitivo: se a troca demorar além do
    // aceitável, a mensagem evolui (estados de carregamento, seção 7).
    const slowTimer = window.setTimeout(() => setSlow(true), SLOW_EXCHANGE_MS);

    // IAM may deliver the minitoken as `token` instead of `minitoken`
    // (federation-bv spec shows `token` on the receiving side). Normalize it
    // here — only on this dedicated route, since `/join` uses `token` for
    // invitations.
    const params = new URLSearchParams(window.location.search);
    if (!params.get('minitoken') && params.get('token')) {
      const url = new URL(window.location.href);
      url.searchParams.set('minitoken', params.get('token') as string);
      url.searchParams.delete('token');
      window.history.replaceState(window.history.state, '', url.toString());
    }

    if (!ChangeAppReceiver.hasMinitoken()) {
      window.clearTimeout(slowTimer);
      setErrorKind('invalid-callback');
      return;
    }

    const receiver = new ChangeAppReceiver({
      bffBaseUrl: import.meta.env?.VITE_API_BASE_URL ?? '',
    });

    const exchangeToken = async (): Promise<void> => {
      try {
        // originClientId is the IAM-SAML API client id (per environment,
        // provided by the AuthMe team) — the SAML callback URL does not
        // carry it, unlike cross-portal change-app redirects.
        const auth = await receiver.renewFromUrl({
          clientId: import.meta.env?.VITE_SSO_CLIENT_ID ?? '',
          originClientId:
            ChangeAppReceiver.extractOriginClientId() ??
            import.meta.env?.VITE_IAM_SAML_CLIENT_ID ??
            undefined,
          userId: ChangeAppReceiver.extractUserId() ?? undefined,
          rememberMe: true,
        });

        if (!(await hasPlatformAccess(auth.accessToken))) {
          useAuthStore.getState().reset();
          // CA11 — o fluxo SSO termina na tela de SSO, não na de login por
          // credencial: o aviso aparece onde o usuário iniciou o acesso.
          navigate('/sso', {
            replace: true,
            state: { notice: 'access-not-granted' },
          });
          return;
        }

        useAuthStore.getState().loginCompleted({
          accessToken: auth.accessToken,
          expiresIn: auth.expiresIn,
          tokenType: auth.tokenType,
          scope: auth.scope ?? [],
        });

        navigate('/', { replace: true });
      } catch (err: unknown) {
        setErrorKind(classifyCallbackError(err));
      } finally {
        window.clearTimeout(slowTimer);
      }
    };

    void exchangeToken();

    return () => window.clearTimeout(slowTimer);
  }, [navigate, t]);

  if (errorKind) {
    const messages: Record<CallbackErrorKind, string> = {
      'invalid-callback': t('Invalid SSO callback'),
      'not-authorized': t(
        'Your access has not been released yet. Contact the access manager at your company.'
      ),
      expired: t('Your sign-in session expired. Start the access again.'),
      unavailable: t(
        "Your company's authentication service is temporarily unavailable. This is not a platform issue — please try again in a few moments."
      ),
      failed: t('SSO sign in failed'),
    };
    // CA11 — ausência de autorização não é falha de autenticação: informa,
    // orienta a procurar o gestor da empresa e não oferece "tentar de novo"
    // (repetir não resolve). Demais categorias reiniciam em uma única ação.
    const showRetry = errorKind !== 'not-authorized';

    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <div className="sso-callback-error">
            <Alert
              message={
                <span role="alert" aria-live="assertive">
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('Attention!')}
                  </span>{' '}
                  {messages[errorKind]}
                </span>
              }
              type={errorKind === 'not-authorized' ? 'warning' : 'error'}
              showIcon
            />
            {showRetry && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => navigate('/sso', { replace: true })}
                className="sso-callback-retry-button"
              >
                {t('Try again')}
              </Button>
            )}
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login', { replace: true })}
              className="sso-callback-back-button"
            >
              {t('Back to Login')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-container">
        <div
          className="sso-callback-loading"
          role="status"
          aria-live="polite"
          aria-label={t('Completing sign in')}
        >
          <Spin size="large" />
          <p className="sso-callback-loading-text">{t('Completing sign in')}</p>
          {slow && (
            <p className="sso-callback-loading-text">
              {t(
                'This is taking longer than expected. Please keep this window open.'
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SsoCallbackPage;
